import { useState, useEffect } from 'react'
import { useAuth } from '../lib/auth'
import { api } from '../lib/api'
import { ApiClientError } from '@hirebridge/api-client'

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string
            callback: (response: { credential: string }) => void
            auto_select?: boolean
            cancel_on_tap_outside?: boolean
          }) => void
          prompt: (notification?: (notification: any) => void) => void
          renderButton: (
            element: HTMLElement,
            options: {
              type?: 'standard' | 'icon'
              theme?: 'outline' | 'filled_blue' | 'filled_black'
              size?: 'large' | 'medium' | 'small'
              text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin'
              shape?: 'rectangular' | 'pill' | 'circle' | 'square'
              logo_alignment?: 'left' | 'center'
              width?: number
            },
          ) => void
        }
        oauth2: {
          initTokenClient: (config: {
            client_id: string
            scope: string
            callback: (tokenResponse: { access_token?: string; error?: string }) => void
            error_callback?: (err: any) => void
          }) => {
            requestAccessToken: (overrideConfig?: { prompt?: string }) => void
          }
        }
      }
    }
  }
}

interface GoogleSignInButtonProps {
  role?: 'candidate' | 'employer' | 'freelancer'
  companyName?: string
  mode?: 'signin' | 'signup'
  onSuccess?: () => void
  onError?: (err: string) => void
  style?: React.CSSProperties
  className?: string
}

export function GoogleSignInButton({
  role = 'candidate',
  companyName,
  mode = 'signin',
  onSuccess,
  onError,
  style,
  className = '',
}: GoogleSignInButtonProps) {
  const { loginWithGoogle } = useAuth()
  const [loading, setLoading] = useState(false)
  const [clientId, setClientId] = useState<string | null>(
    import.meta.env.VITE_GOOGLE_CLIENT_ID || null,
  )

  useEffect(() => {
    // If not in env, check if backend has client_id configured
    if (!clientId) {
      void api.googleAuthUrl().then((res) => {
        if (res?.client_id) setClientId(res.client_id)
      }).catch(() => {})
    }
  }, [clientId])

  const handleGoogleSignIn = async () => {
    setLoading(true)

    try {
      // 1. Ensure Google Identity Services SDK is loaded
      if (!window.google?.accounts) {
        // Load dynamically if not ready
        await new Promise<void>((resolve, reject) => {
          const script = document.createElement('script')
          script.src = 'https://accounts.google.com/gsi/client'
          script.async = true
          script.defer = true
          script.onload = () => resolve()
          script.onerror = () => reject(new Error('Failed to load Google Sign-In SDK'))
          document.head.appendChild(script)
        })
      }

      const activeClientId = clientId || import.meta.env.VITE_GOOGLE_CLIENT_ID

      if (!activeClientId) {
        // Check backend authorization URL
        const authUrlData = await api.googleAuthUrl().catch(() => null)
        if (authUrlData?.url) {
          window.location.href = authUrlData.url
          return
        }
        throw new Error(
          'Google Client ID is not configured. Please set GOOGLE_CLIENT_ID and VITE_GOOGLE_CLIENT_ID in your environment.',
        )
      }

      // 2. Trigger real Google OAuth popup with browser's logged-in account
      const tokenClient = window.google!.accounts.oauth2.initTokenClient({
        client_id: activeClientId,
        scope: 'email profile openid',
        callback: async (tokenResponse) => {
          if (tokenResponse.error) {
            setLoading(false)
            onError?.(tokenResponse.error)
            return
          }

          if (!tokenResponse.access_token) {
            setLoading(false)
            onError?.('No access token received from Google')
            return
          }

          try {
            // Fetch verified user profile from Google's API
            const userInfoRes = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
              headers: {
                Authorization: `Bearer ${tokenResponse.access_token}`,
              },
            })

            if (!userInfoRes.ok) {
              throw new Error('Failed to retrieve user profile from Google')
            }

            const profile = await userInfoRes.json()

            // Login or register user with real Google account details
            await loginWithGoogle({
              token: tokenResponse.access_token,
              email: profile.email,
              first_name: profile.given_name || profile.name || 'Google',
              last_name: profile.family_name || '',
              avatar_url: profile.picture,
              role,
              company_name: companyName,
            })

            onSuccess?.()
          } catch (err: any) {
            const message =
              err instanceof ApiClientError
                ? err.message
                : err?.message || 'Authentication with Google failed.'
            onError?.(message)
          } finally {
            setLoading(false)
          }
        },
        error_callback: (err) => {
          setLoading(false)
          onError?.(err?.message || 'Google Sign-In was closed or cancelled.')
        },
      })

      // Request access token with account selection prompt
      tokenClient.requestAccessToken({ prompt: 'select_account' })
    } catch (err: any) {
      setLoading(false)
      const message =
        err instanceof ApiClientError
          ? err.message
          : err?.message || 'Unable to open Google Sign-In.'
      onError?.(message)
    }
  }

  return (
    <button
      type="button"
      className={`google-oauth-btn ${className}`}
      style={style}
      onClick={handleGoogleSignIn}
      disabled={loading}
    >
      <span className="google-icon-wrap">
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M17.64 9.20455C17.64 8.56636 17.5827 7.95273 17.4764 7.36364H9V10.845H13.8436C13.635 11.97 13.0009 12.9232 12.0477 13.5614V15.8195H14.9564C16.6582 14.2527 17.64 11.9455 17.64 9.20455Z"
            fill="#4285F4"
          />
          <path
            d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5614C11.2418 14.1014 10.2109 14.4205 9 14.4205C6.65591 14.4205 4.67182 12.8373 3.96409 10.71H0.957275V13.0418C2.43818 15.9832 5.48182 18 9 18Z"
            fill="#34A853"
          />
          <path
            d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29V4.95818H0.957275C0.347727 6.17318 0 7.54773 0 9C0 10.4523 0.347727 11.8268 0.957275 13.0418L3.96409 10.71Z"
            fill="#FBBC05"
          />
          <path
            d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957275 4.95818L3.96409 7.29C4.67182 5.16273 6.65591 3.57955 9 3.57955Z"
            fill="#EA4335"
          />
        </svg>
      </span>
      <span>
        {loading
          ? 'Connecting to Google…'
          : mode === 'signup'
          ? 'Sign up with Google'
          : 'Continue with Google'}
      </span>
    </button>
  )
}
